import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Paper,
  Chip,
  Container,
  Typography,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';

import ArrowForwardTwoToneIcon from '@mui/icons-material/ArrowForwardTwoTone';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BoltIcon from '@mui/icons-material/Bolt';
import Co2Icon from '@mui/icons-material/Co2';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

import { blue, lightBlue, grey, blueGrey } from '@mui/material/colors';

import BgVideo from '../../resources/videos/daclair_video.mp4';
import SpecTable from '../../components/product/SpecTable';
import FirstEnviromentContainer from '../../components/product/FirstEnviromentContainer';
import SecondEnviromentContainer from '../../components/product/SecondEnviromentContainer';
import ThirdEnviromentContainer from '../../components/product/ThirdEnviromentContainer';

import moment from 'moment';
import 'moment/locale/ko';

function ScrollReveal({ children, delay = 0, direction = 'up' }) {
  const variants = {
    up: { hidden: { opacity: 0, y: 60 }, visible: { opacity: 1, y: 0, x: 0 } },
    left: { hidden: { opacity: 0, x: -60 }, visible: { opacity: 1, y: 0, x: 0 } },
    right: { hidden: { opacity: 0, x: 60 }, visible: { opacity: 1, y: 0, x: 0 } },
  };
  return (
    <motion.div
      variants={variants[direction]}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {children}
    </motion.div>
  );
}

const FEATURES = [
  { img: '/assets/images/daclair_des2.png', title: '안전한 컵 세척', desc: '6개의 다리는 다양한 컵과 텀블러를 안전하게 세척할 수 있는 구조로, 강한 수압으로도 쉽게 넘어지지 않습니다.' },
  { img: '/assets/images/daclair_des3.png', title: '분리 세척이 가능', desc: '스테인리스로 상단을 구성하였으며, 상단을 쉽게 분리할 수 있어서 세척에 용이합니다.' },
  { img: '/assets/images/daclair_des4.png', title: '위생 도기로 제작', desc: '위생 도기로 제품을 구성하여 외관과 위를 모두 물 세척할 수 있습니다.', footnote: '* 테이블형 및 제작형은 상단만 위생도기로 구성됩니다.' },
  { img: '/assets/images/daclair_des5.png', title: 'UVC 소독 효과', desc: '3개의 UVC LED(275nm)가 세척과 동시에 내부의 소독을 도와줍니다.' },
  { img: '/assets/images/daclair_des6.png', title: 'QR 코드 및 바코드 리더', desc: 'QR 코드 리더기를 통해 세척하는 텀블러 및 유저를 특정하여 Daclair 포인트 적립 등 여러 서비스에 이용됩니다.', footnote: '* Daclair 서비스의 포인트 적립 용도로 사용됩니다.' },
  { img: '/assets/images/daclair_display.png', title: '터치 버튼 및 LED 패널', desc: '여러 LED와 터치 버튼을 통해 텀블러 세척기가 가지고 있는 다양한 기능들을 표기하고 구성합니다.' },
];

const VARIANTS = [
  { img: '/assets/images/daclair_option1.png', name: '독립형', desc: '단독으로 동작하는 기기입니다.\n사무실 및 공용시설에 적합합니다.' },
  { img: '/assets/images/daclair_option2.png', name: '테이블형', desc: '원하는 위치에 설치가 가능합니다.\n인테리어에 맞추어 설치가 가능합니다.\n카페와 같은 곳에 추천합니다.' },
  { img: '/assets/images/daclair_option3.png', name: '멀티 테이블형', desc: '테이블형의 확장으로 유동인구가 많은 곳에 용이합니다.' },
];

const ENV_STATS = [
  { icon: <WaterDropIcon />, value: '98%', label: '절약된 물', color: '#29b6f6' },
  { icon: <BoltIcon />, value: '60%', label: '전기 절약', color: '#ffb300' },
  { icon: <Co2Icon />, value: '500g', label: '세척당 CO₂ 절감', color: '#66bb6a' },
  { icon: <QrCodeScannerIcon />, value: '3초', label: 'QR 스캔으로 시작', color: '#ab47bc' },
];

function CustomTabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function TumblerWasherPage() {
  const [tabValue, setTabValue] = React.useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const heroRef = useRef(null);
  const videoRef = useRef(null);
  const navigate = useNavigate();

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  // 모바일 포함 모든 환경에서 자동재생 강제 시도
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = () => {
      video.play().then(() => {
        setVideoReady(true);
      }).catch(() => {
        // 자동재생 차단 시 포스터 이미지로 대체
        setVideoReady(false);
      });
    };

    if (video.readyState >= 3) {
      tryPlay();
    } else {
      video.addEventListener('canplaythrough', tryPlay, { once: true });
    }

    return () => video.removeEventListener('canplaythrough', tryPlay);
  }, []);

  return (
    <>
      {/* ── 히어로: 전체화면 비디오 ── */}
      <Box ref={heroRef} sx={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden', backgroundColor: '#111' }}>

        {/* 포스터 배경 (비디오 로드 전 또는 재생 차단 시) */}
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(/assets/images/daclair_image1.png)',
          backgroundSize: 'cover', backgroundPosition: 'center',
          opacity: videoReady ? 0 : 1,
          transition: 'opacity 0.6s ease',
        }} />

        {/* 비디오 레이어 */}
        <Box component={motion.div} style={{ scale: videoScale }} sx={{ position: 'absolute', inset: 0, transformOrigin: 'center' }}>
          <video
            ref={videoRef}
            loop
            muted
            playsInline
            preload="auto"
            poster="/assets/images/daclair_image1.png"
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              opacity: videoReady ? 1 : 0,
              transition: 'opacity 0.6s ease',
            }}
          >
            <source src={BgVideo} type="video/mp4" />
          </video>
        </Box>

        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.85) 100%)' }} />

        <Box component={motion.div} style={{ y: heroTextY, opacity: heroOpacity }}
          sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: { xs: 3, md: 6 } }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: { xs: '11px', md: '13px' }, color: 'rgba(100,200,255,0.8)', letterSpacing: '5px', textTransform: 'uppercase', mb: 3 }}>
              Daclair Tumbler Washer
            </Typography>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: { xs: '36px', sm: '52px', md: '68px' }, color: '#ffffff', lineHeight: 1.15, letterSpacing: { xs: '-1px', md: '-2px' }, mb: 3 }}>
              탄소 중립의<br />
              <Box component="span" sx={{ background: 'linear-gradient(90deg, #4fc3f7, #81d4fa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                새로운 시작
              </Box>
            </Typography>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 300, fontSize: { xs: '15px', md: '18px' }, color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, mb: 3 }}>
              텀블러 세척기로 시작하는 일상 속 환경 실천
            </Typography>
            <img src="/assets/icons/daclair_tb_logo_white_under_1028.png" alt="Daclair" style={{ height: '28px', objectFit: 'contain', opacity: 0.7, marginBottom: '28px', display: 'block', margin: '0 auto 28px' }} />

            {/* 구입 문의 버튼 */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="center"
              alignItems="center">
              <Button
                variant="contained"
                startIcon={<ShoppingCartOutlinedIcon />}
                onClick={() => navigate('/company/contact?service=tumbler_washer&type=purchase')}
                sx={{
                  fontFamily: 'NotoSansKR',
                  fontWeight: 800,
                  fontSize: { xs: '14px', md: '15px' },
                  px: { xs: 3.5, md: 4 },
                  py: 1.4,
                  borderRadius: '50px',
                  backgroundColor: '#0288D1',
                  boxShadow: '0 0 28px rgba(2,136,209,0.45)',
                  letterSpacing: '0.3px',
                  '&:hover': {
                    backgroundColor: '#0277BD',
                    boxShadow: '0 0 40px rgba(2,136,209,0.65)',
                  },
                }}>
                구입 문의하기
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  document.querySelector('#product-features')?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{
                  fontFamily: 'NotoSansKR',
                  fontWeight: 700,
                  fontSize: { xs: '14px', md: '15px' },
                  px: { xs: 3.5, md: 4 },
                  py: 1.4,
                  borderRadius: '50px',
                  color: 'rgba(255,255,255,0.85)',
                  borderColor: 'rgba(255,255,255,0.35)',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.8)',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                  },
                }}>
                제품 살펴보기
              </Button>
            </Stack>
          </motion.div>
        </Box>

        <Box component={motion.div} style={{ opacity: heroOpacity }} animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          sx={{ position: 'absolute', bottom: '36px', left: 'calc(50% - 29.5px)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'NotoSansKR', fontSize: '11px', color: 'rgba(255,255,255,0.3)', letterSpacing: '3px', textTransform: 'uppercase' }}>Scroll</Typography>
          <KeyboardArrowDownIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '20px' }} />
        </Box>
      </Box>

      {/* ── 도입문 + 지표 ── */}
      <Box sx={{ backgroundColor: '#ffffff', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="md">
          <ScrollReveal>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: { xs: '12px', md: '13px' }, color: blue[400], letterSpacing: '4px', textTransform: 'uppercase', textAlign: 'center', mb: 2 }}>
              친환경 텀블러 세척기
            </Typography>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: { xs: '28px', sm: '38px', md: '48px' }, textAlign: 'center', color: blueGrey[900], lineHeight: 1.25, mb: 3 }}>
              환경을 항상 생각하며<br />만듭니다
            </Typography>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: { xs: '15px', md: '18px' }, textAlign: 'center', lineHeight: 1.9, color: blueGrey[600], maxWidth: '680px', mx: 'auto', mb: 6 }}>
              텀블러 사용은 일회용품을 대체하는 탄소 중립의 첫 걸음입니다.
              Daclair 텀블러 세척기는 강한 세척력으로 불편함을 해결하며,
              물과 에너지 절약으로 탄소 중립을 실천합니다.
            </Typography>
          </ScrollReveal>

          <Grid container spacing={2}>
            {ENV_STATS.map((stat, i) => (
              <Grid size={{ xs: 6, sm: 3 }} key={stat.label}>
                <ScrollReveal delay={i * 0.12}>
                  <Box sx={{ textAlign: 'center', py: 3, px: 2, backgroundColor: `${stat.color}15`, borderRadius: '16px', border: `1px solid ${stat.color}30` }}>
                    <Box sx={{ color: stat.color, mb: 1, '& .MuiSvgIcon-root': { fontSize: '28px' } }}>{stat.icon}</Box>
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '26px', color: blueGrey[900] }}>{stat.value}</Typography>
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: '12px', color: blueGrey[500], mt: 0.5 }}>{stat.label}</Typography>
                  </Box>
                </ScrollReveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── 제품 이미지 ── */}
      <Box sx={{ position: 'relative', width: '100%', height: { xs: '50vw', md: '60vh' }, overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
        <motion.div whileInView={{ scale: 1 }} initial={{ scale: 1.06 }} viewport={{ once: true }} transition={{ duration: 1.2 }} style={{ position: 'absolute', inset: 0 }}>
          <img src="/assets/images/daclair_image1.png" alt="Daclair 텀블러 세척기" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </motion.div>
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '35%', background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)' }} />
      </Box>

      {/* ── 기능 그리드 ── */}
      <Box id="product-features" sx={{ backgroundColor: '#ffffff', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <ScrollReveal>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, textAlign: 'center', fontSize: { xs: '26px', md: '38px' }, mb: 2, color: blueGrey[800] }}>
              최적의 텀블러 세척기
            </Typography>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, textAlign: 'center', fontSize: { xs: '15px', md: '18px' }, mb: 7, color: blueGrey[500] }}>
              사용자의 편의를 위해 다양한 기능을 탑재했습니다
            </Typography>
          </ScrollReveal>

          <Grid container spacing={3}>
            {FEATURES.map((feat, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={feat.title}>
                <ScrollReveal delay={i * 0.1}>
                  <Box sx={{ borderRadius: '20px', overflow: 'hidden', backgroundColor: '#fafafa', border: `1px solid ${grey[100]}`, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 20px 40px rgba(0,0,0,0.08)', borderColor: lightBlue[200] } }}>
                    <Box sx={{ position: 'relative', width: '100%', paddingBottom: '70%', overflow: 'hidden' }}>
                      <img src={feat.img} alt={feat.title} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                    <Box sx={{ p: 3 }}>
                      <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '18px', color: blueGrey[800], mb: 1 }}>{feat.title}</Typography>
                      <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: '14px', color: blueGrey[600], lineHeight: 1.7 }}>{feat.desc}</Typography>
                      {feat.footnote && <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 300, fontSize: '12px', color: blueGrey[400], mt: 1 }}>{feat.footnote}</Typography>}
                    </Box>
                  </Box>
                </ScrollReveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── 제품 유형 ── */}
      <Box sx={{ backgroundColor: '#f8fafc', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <ScrollReveal>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, textAlign: 'center', fontSize: { xs: '26px', md: '36px' }, mb: 1.5, color: blueGrey[800] }}>
              나에게 맞는 텀블러 세척기 찾기
            </Typography>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, textAlign: 'center', fontSize: { xs: '15px', md: '18px' }, mb: 7, color: blueGrey[500] }}>
              상황과 환경에 따라 텀블러 세척기를 선택할 수 있습니다
            </Typography>
          </ScrollReveal>
          <Grid container spacing={3} justifyContent="center">
            {VARIANTS.map((v, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={v.name}>
                <ScrollReveal delay={i * 0.15}>
                  <Paper elevation={0} sx={{ borderRadius: '20px', overflow: 'hidden', border: `1px solid ${grey[200]}`, transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 16px 40px rgba(0,0,0,0.12)', transform: 'translateY(-4px)', borderColor: lightBlue[300] } }}>
                    <Box sx={{ position: 'relative', width: '100%', paddingBottom: '80%' }}>
                      <img src={v.img} alt={v.name} style={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                    <Box sx={{ p: 3 }}>
                      <Chip label={v.name} size="small" sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '12px', backgroundColor: lightBlue[50], color: lightBlue[700], mb: 1.5 }} />
                      <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: '14px', color: blueGrey[600], lineHeight: 1.8, whiteSpace: 'pre-line' }}>{v.desc}</Typography>
                    </Box>
                  </Paper>
                </ScrollReveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── 환경 컨테이너 ── */}
      <FirstEnviromentContainer />
      <SecondEnviromentContainer />
      <ThirdEnviromentContainer />

      {/* ── 사양 탭 ── */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <ScrollReveal>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, textAlign: 'center', fontSize: { xs: '22px', md: '28px' }, mb: '32px', color: blueGrey[600] }}>
              텀블러 세척기에 대해 더 알아보세요
            </Typography>
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} centered>
              <Tab label="사양" sx={{ fontFamily: 'NotoSansKR', fontWeight: 900 }} />
              <Tab label="악세서리" sx={{ fontFamily: 'NotoSansKR', fontWeight: 900 }} />
              <Tab label="부가사항" sx={{ fontFamily: 'NotoSansKR', fontWeight: 900 }} />
            </Tabs>
            <CustomTabPanel value={tabValue} index={0}>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '28px', color: '#222', my: '24px' }}>제품 사양</Typography>
              <SpecTable />
            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={1}><Box /></CustomTabPanel>
            <CustomTabPanel value={tabValue} index={2}><Box /></CustomTabPanel>
          </ScrollReveal>
        </Container>
      </Box>

      {/* ── CTA 섹션 ── */}
      <Box sx={{ backgroundColor: blueGrey[900], py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <ScrollReveal>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, textAlign: 'center', fontSize: { xs: '22px', md: '30px' }, color: '#ffffff', mb: 4 }}>
              처음 사용하시거나 사용 방법을 잘 모르시나요?
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button variant="outlined" href="/manuals" sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '15px', px: 4, py: 1.5, borderRadius: '50px', color: '#ffffff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' } }}>
                사용 매뉴얼 보기
              </Button>
              <Button variant="contained" endIcon={<ArrowForwardTwoToneIcon />} href="/company/contact?service=tumbler_washer" sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '15px', px: 4, py: 1.5, borderRadius: '50px', backgroundColor: lightBlue[500], '&:hover': { backgroundColor: lightBlue[400] } }}>
                고객센터 문의
              </Button>
            </Stack>
          </ScrollReveal>
        </Container>
      </Box>
    </>
  );
}

export default TumblerWasherPage;
